import React, { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { appRouter } from './app/routes';
import { ColorSchemeScript, MantineProvider } from '@mantine/core';
import { COLOR_SCHEME_STORAGE_KEY, getAppMantineProps } from './theme';
import { Notifications } from '@mantine/notifications';
import { AppErrorBoundary } from './components/errors/AppErrorBoundary';
import PortalLandingPage from './pages/auth/PortalLandingPage';
import LogoutPage from './pages/logout/LogoutPage';
import { isLogoutPath } from './auth/logoutPaths';
import {
  clearSchedulerLoggedOutFlag,
  isPortalHandoff,
  isSchedulerLoggedOut,
  markSchedulerSessionActive,
  stripPortalHandoffFromUrl,
} from './auth/schedulerSession';
import {
  applyPortalSessionFromCookies,
  getAuthToken,
  getCustomerId,
  getProjectId,
} from './utils/cookies';
import { ModalContainerProvider } from './contexts/ModalContainerContext';
import { perfMonitor } from './utils/performanceMarkers';
import { setTransactionServerAuthToken } from './api/schedulingApis';
import { setRequestTrackingAuthToken } from './features/request-tracking';
import { PortalProjectProvider, usePortalProject } from './contexts/PortalProjectContext';
import { ResourcePermissionsBootstrap } from './components/auth/ResourcePermissionsBootstrap';
import { handlePortalScopeChange } from './utils/portalScopeChange';
import '@mantine/core/styles.css';
import '@mantine/charts/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@dhx/react-gantt/dist/react-gantt.css';
import './styles/app-theme.css';
import './styles/gantt-custom.css';
import './styles/location-hierarchy.css';

/** Above stacked modals. Predecessor picker and Assign users use 1200; their dropdowns use 1300. */
const NOTIFICATIONS_Z_INDEX = 2000;

const mantineProviderProps = getAppMantineProps();

if (typeof window !== 'undefined') {
  (window as any).perfMonitor = perfMonitor;
}

type BootState = 'loading' | 'ready' | 'landing';

function LogoutShell({ children }: { children: React.ReactNode }) {
  return (
    <StrictMode>
      <MantineProvider {...mantineProviderProps}>{children}</MantineProvider>
    </StrictMode>
  );
}

function App() {
  if (isLogoutPath()) {
    return (
      <LogoutShell>
        <LogoutPage />
      </LogoutShell>
    );
  }

  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const modalContainer = document.getElementById('modal-root');
  const [bootState, setBootState] = useState<BootState>('loading');

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (isPortalHandoff()) {
        clearSchedulerLoggedOutFlag();
      }

      if (isSchedulerLoggedOut() && !isPortalHandoff()) {
        if (!cancelled) {
          setBootState('landing');
        }
        return;
      }

      const applied = applyPortalSessionFromCookies();

      if (applied.scopeChanged && applied.previousProjectId) {
        await handlePortalScopeChange(applied.previousProjectId);
      }

      const authToken = getAuthToken();
      const projectId = getProjectId();
      const customerId = getCustomerId();

      if (!authToken || !projectId || !customerId) {
        if (!cancelled) {
          setBootState('landing');
        }
        return;
      }

      try {
        setTransactionServerAuthToken(authToken);
        setRequestTrackingAuthToken(authToken);
        markSchedulerSessionActive();
        stripPortalHandoffFromUrl();
        if (!cancelled) {
          setBootState('ready');
        }
      } catch (error) {
        console.error('[App] Failed to initialize auth context:', error);
        if (!cancelled) {
          setBootState('landing');
        }
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  if (bootState === 'landing') {
    return (
      <StrictMode>
        <MantineProvider {...mantineProviderProps}>
          <ModalContainerProvider container={modalContainer}>
            <PortalLandingPage />
          </ModalContainerProvider>
        </MantineProvider>
      </StrictMode>
    );
  }

  if (bootState !== 'ready') {
    return (
      <StrictMode>
        <MantineProvider {...mantineProviderProps}>
          <div
            style={{
              minHeight: '100vh',
              padding: '40px',
              textAlign: 'center',
              backgroundColor: 'var(--app-shell-bg, var(--mantine-color-gray-1))',
            }}
          >
            <h2>Loading...</h2>
          </div>
        </MantineProvider>
      </StrictMode>
    );
  }

  return (
    <StrictMode>
      <ModalContainerProvider container={modalContainer}>
        <MantineProvider {...mantineProviderProps}>
          <Notifications zIndex={NOTIFICATIONS_Z_INDEX} />
          <PortalProjectProvider>
            <ResourcePermissionsBootstrap>
              <AppErrorBoundary>
                <PortalAppRouter />
              </AppErrorBoundary>
            </ResourcePermissionsBootstrap>
          </PortalProjectProvider>
        </MantineProvider>
      </ModalContainerProvider>
    </StrictMode>
  );
}

function PortalAppRouter() {
  const { projectId } = usePortalProject();
  return <RouterProvider key={projectId || 'no-project'} router={appRouter} />;
}

createRoot(document.getElementById('root')!).render(
  <>
    <ColorSchemeScript defaultColorScheme="auto" localStorageKey={COLOR_SCHEME_STORAGE_KEY} />
    <App />
  </>
);
